import React, { useState, useEffect } from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import WordTranslationExercise from './WordTranslationExercise';
import './WordTranslationEmbed.css';
import { useExerciseSync } from '../../context/ExerciseSyncContext';

const WORD_TRANSLATION_ICON = (
  <span className="default-icon-spacing" style={{
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 48, height: 48, borderRadius: 12, background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)'
  }}>
    <svg width="28" height="28" viewBox="0 0 512 512" fill="white">
      <g>
        <g>
          <path d="m385.421 379.639-20.889-41.778-20.889 41.778z"/>
        </g>
        <g>
          <path d="m467.019 217.065h-21.252v-44.963c0-4.242-1.796-8.285-4.943-11.129s-7.354-4.221-11.571-3.794c-2.388.242-55.886 6.292-82.521 59.886h-21.796v32.889c0 41.345-33.637 74.981-74.981 74.981h-32.889v142.083c0 24.803 20.179 44.981 44.981 44.981h204.972c24.802.001 44.981-20.178 44.981-44.98v-204.972c0-24.803-20.179-44.982-44.981-44.982zm-176.116 200.972 60.212-120.425c2.541-5.082 7.735-8.292 13.417-8.292s10.875 3.21 13.417 8.292l60.213 120.425c3.705 7.41.701 16.419-6.708 20.125s-16.42.702-20.125-6.708l-10.907-21.814h-71.778l-10.907 21.814c-2.628 5.256-7.926 8.295-13.428 8.295-2.254 0-4.543-.51-6.696-1.587-7.411-3.705-10.414-12.715-6.71-20.125z"/>
        </g>
        <g>
          <path d="m161.681 126.34h-28.427c1.856 14.861 6.613 27.356 14.214 37.424 7.6-10.068 12.357-22.563 14.213-37.424z"/>
        </g>
        <g>
          <path d="m249.954 294.935c24.803 0 44.981-20.179 44.981-44.981v-204.972c0-24.803-20.179-44.982-44.981-44.982h-204.972c-24.803 0-44.982 20.179-44.982 44.982v204.972c0 24.803 20.179 44.981 44.981 44.981h21.252v44.963c0 4.242 1.796 8.285 4.943 11.129 2.77 2.503 6.358 3.871 10.056 3.871.503 0 1.01-.025 1.515-.077 2.388-.242 55.886-6.292 82.521-59.886zm-94.492-86.062c-2.624-1.4-5.304-2.981-7.994-4.74-2.691 1.759-5.37 3.341-7.994 4.74-25.594 13.65-51.143 13.807-52.218 13.807-8.284 0-15-6.716-15-15s6.716-15 15-15c.132-.001 18.639-.27 37.054-9.732-10.536-13.492-18.84-31.799-21.239-56.608h-15.816c-8.284 0-15-6.716-15-15s6.716-15 15-15h45.213v-9.085c0-8.284 6.716-15 15-15s15 6.716 15 15v9.085h45.212c8.284 0 15 6.716 15 15s-6.716 15-15 15h-15.814c-2.4 24.809-10.703 43.116-21.239 56.608 18.414 9.462 36.924 9.73 37.115 9.732 8.284 0 14.969 6.716 14.969 15s-6.747 15-15.031 15c-1.075 0-26.624-.156-52.218-13.807z"/>
        </g>
        <g>
          <path d="m365.669 85.819c2.929 2.929 6.768 4.394 10.606 4.394s7.678-1.464 10.606-4.394c5.858-5.858 5.858-15.355 0-21.213l-4.306-4.306c55.209 3.271 99.125 49.215 99.125 105.231 0 8.284 6.716 15 15 15s15-6.716 15-15c0-72.673-57.543-132.151-129.458-135.286l4.639-4.639c5.858-5.858 5.858-15.355 0-21.213-5.857-5.857-15.355-5.858-21.213 0l-30.105 30.107c-5.858 5.858-5.858 15.355 0 21.213z"/>
        </g>
        <g>
          <path d="m146.032 425.881c-5.857-5.858-15.355-5.858-21.213 0s-5.858 15.355 0 21.213l4.306 4.306c-55.209-3.27-99.125-49.215-99.125-105.231 0-8.284-6.716-15-15-15s-15 6.716-15 15c0 72.673 57.543 132.151 129.458 135.286l-4.639 4.639c-5.858 5.858-5.858 15.355 0 21.213 2.929 2.929 6.768 4.393 10.606 4.393s7.678-1.464 10.606-4.394l30.106-30.106c5.858-5.858 5.858-15.355 0-21.213z"/>
        </g>
      </g>
    </svg>
  </span>
);

const WordTranslationEmbed = ({ node, updateAttributes, editor, getPos }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const exerciseIdRef = React.useRef(node.attrs.exerciseId);
  if (!exerciseIdRef.current) exerciseIdRef.current = crypto.randomUUID();
  const exerciseId = exerciseIdRef.current;
  useEffect(() => {
    if (!node.attrs.exerciseId) updateAttributes({ exerciseId });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const { myState, otherState, updateState, liveUpdateCount } = useExerciseSync(exerciseId);

  // Force ProseMirror to re-measure after expand/collapse so click coordinates stay accurate
  useEffect(() => {
    if (!editor) return;
    const id = requestAnimationFrame(() => {
      try {
        editor.view.updateState(editor.view.state);
        // Mobile Safari/Chrome: re-syncing the view can leave the browser
        // thinking the (still logically-selected) editable region wants the
        // on-screen keyboard, even though the user only tapped this
        // exercise's own contentEditable=false UI. Blur ONLY if the outer
        // editor root itself still has focus (not an input inside the
        // exercise's own edit mode), so the keyboard does not pop on
        // expand/collapse.
        if (typeof document !== 'undefined' && document.activeElement === editor.view.dom) {
          editor.view.dom.blur();
        }
      } catch (_) {}
    });
    return () => cancelAnimationFrame(id);
  }, [isExpanded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mirror a REMOTE peer's expand/collapse live, without replaying stale
  // localStorage-hydrated data on every remount — see ExerciseSyncContext's
  // liveUpdateCount for why this can't just watch otherState.expanded alone.
  const lastLiveUpdateCountRef = React.useRef(liveUpdateCount);
  useEffect(() => {
    if (liveUpdateCount === lastLiveUpdateCountRef.current) return;
    lastLiveUpdateCountRef.current = liveUpdateCount;
    if (otherState.expanded !== undefined && otherState.expanded !== isExpanded) {
      setIsExpanded(otherState.expanded);
    }
  }, [liveUpdateCount]); // eslint-disable-line react-hooks/exhaustive-deps

  const { pairs, title, targetLanguage } = node.attrs;

  const isStudent = (() => {
    try {
      const root = editor?.view?.dom;
      const fromRoot = root?.getAttribute?.('data-student-mode');
      if (fromRoot) return fromRoot === 'true';
      return typeof document !== 'undefined' &&
        (window.location.pathname.includes('/student-dashboard') ||
         window.location.pathname.includes('/student-profile'));
    } catch (_) { return false; }
  })();

  const isHomework = (() => {
    try {
      const root = editor?.view?.dom;
      const fromRoot = root?.getAttribute?.('data-tab-type');
      if (fromRoot) return fromRoot === 'homework';
      const fromBody = typeof document !== 'undefined' ? document.body.getAttribute('data-novas-current-tab-type') : null;
      return fromBody === 'homework';
    } catch (_) { return false; }
  })();

  const insertParagraphAt = (pos) => {
    if (!editor || typeof pos !== 'number') return;
    editor.chain().focus().insertContentAt(pos, { type: 'paragraph' }).setTextSelection(pos + 1).run();
  };

  const handleInsertAbove = (e) => {
    e.preventDefault();
    e.stopPropagation();
    insertParagraphAt(typeof getPos === 'function' ? getPos() : null);
  };

  const handleInsertBelow = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const base = typeof getPos === 'function' ? getPos() : null;
    if (typeof base === 'number' && node && typeof node.nodeSize === 'number') {
      insertParagraphAt(base + node.nodeSize);
    }
  };

  const handleToggle = () => {
    const next = !isExpanded;
    setIsExpanded(next);
    updateState({ expanded: next });
  };

  const handleClose = () => {
    setIsExpanded(false);
    updateState({ expanded: false });
  };

  const handleDragStart = (e) => {
    e.target.style.opacity = '0.5';
    e.target.style.transform = 'scale(0.95)';
    e.target.style.outline = 'none';
    e.target.style.border = 'none';
    e.target.style.boxShadow = 'none';
    e.target.classList.add('dragging');
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '';
    e.target.style.transform = '';
    e.target.style.outline = '';
    e.target.style.border = '';
    e.target.style.boxShadow = '';
    e.target.classList.remove('dragging');
  };

  if (!pairs || !Array.isArray(pairs)) {
    return (
      <NodeViewWrapper>
        <div className="word-translation-embed-error">
          <p>Error: Invalid Word Translation exercise data</p>
        </div>
      </NodeViewWrapper>
    );
  }

  // Only mark the outer wrapper / preview as a drag handle when the
  // embed is collapsed. With the expanded view also flagged as
  // data-drag-handle, ProseMirror treats clicks on the editing inputs
  // as node-selection / drag-start gestures — the input loses focus,
  // the page scrolls to the node anchor, and the embed collapses back
  // to preview. Restricting drag to the collapsed state fixes editing
  // without removing the ability to reorder the exercise in the document.
  const dragProps = isExpanded
    ? {}
    : { draggable: true, 'data-drag-handle': '', onDragStart: handleDragStart, onDragEnd: handleDragEnd };

  return (
    <NodeViewWrapper className="word-translation-embed" contentEditable={false} {...dragProps}>
      <div className="embed-insert-zone top" onMouseDown={handleInsertAbove} />
      <div className="word-translation-exercise-embed" {...dragProps}>
        {!isExpanded ? (
          <div className="embed-preview unified-exercise-preview" onClick={handleToggle} {...dragProps}>
            {WORD_TRANSLATION_ICON}
            <div className="unified-preview-main">
              <div className="unified-preview-title">{title || 'Word Translation Matching'}</div>
              <div className="unified-preview-meta">
                {pairs.length} pairs · {targetLanguage}
              </div>
            </div>
            <div className="unified-preview-actions" onClick={e => e.stopPropagation()}>
              <button
                className="unified-icon-btn"
                title="Add to homework"
                style={{ display: (isHomework || isStudent) ? 'none' : 'inline-flex' }}
                onClick={() => {
                  try {
                    window.dispatchEvent(new CustomEvent('novas:add-to-homework', {
                      detail: { type: 'wordTranslation', attrs: { pairs, title, targetLanguage } }
                    }));
                  } catch (_) {}
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="white" viewBox="0 0 16 16">
                  <path d="M8.707 1.5a1 1 0 0 0-1.414 0L.646 8.146a.5.5 0 0 0 .708.708L2 8.207V13.5A1.5 1.5 0 0 0 3.5 15h4a.5.5 0 1 0 0-1h-4a.5.5 0 0 1-.5-.5V7.207l5-5 6.646 6.647a.5.5 0 0 0 .708-.708L13 5.793V2.5a.5.5 0 0 0-.5-.5h-1a.5.5 0 0 0-.5.5v1.293z"/>
                  <path d="M16 12.5a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0m-3.5-2a.5.5 0 0 0-.5.5v1h-1a.5.5 0 0 0 0 1h1v1a.5.5 0 1 0 1 0v-1h1a.5.5 0 1 0 0-1h-1v-1a.5.5 0 0 0-.5-.5"/>
                </svg>
              </button>
              <button
                className="unified-icon-btn"
                title="Delete"
                style={{ display: isStudent ? 'none' : 'inline-flex' }}
                onClick={() => editor?.chain().focus().deleteRange({ from: getPos(), to: getPos() + node.nodeSize }).run()}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="white" viewBox="0 0 16 16">
                  <path d="M2.5 1a1 1 0 0 0-1 1v1a1 1 0 0 0 1 1H3v9a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V4h.5a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H10a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1zm3 4a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 .5-.5M8 5a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7A.5.5 0 0 1 8 5m3 .5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 1 0"/>
                </svg>
              </button>
            </div>
          </div>
        ) : (
          <div className="wte-expanded">
            <WordTranslationExercise
              pairs={pairs}
              title={title}
              targetLanguage={targetLanguage}
              onClose={handleClose}
              updateAttributes={updateAttributes}
              node={node}
              editor={editor}
              getPos={getPos}
              isStudent={isStudent}
              myState={myState}
              otherState={otherState}
              updateState={updateState}
            />
          </div>
        )}
      </div>
      <div className="embed-insert-zone bottom" onMouseDown={handleInsertBelow} />
    </NodeViewWrapper>
  );
};

export default WordTranslationEmbed;
